import {
  CategorySummary,
  CommonCategoryKey,
  ComparerNode,
  ComparerParam,
  ComparerPrompts,
  ComparerResults,
  ComparerScopedVariable,
  ComparerStatus,
  ComparerSubroutine,
  ComparerValues,
  ComparerVariables
} from '@/types/comparer';
import { SubroutineId } from '@/types/subroutine';
import { isVariable } from '@/util/typeGuards';

export const makeParam = (key: string, oldValue: unknown, newValue: unknown, changed?: boolean): ComparerParam => ({
  key,
  oldValue,
  newValue,
  changed: changed ?? oldValue !== newValue
});

export const flattenValue = (value: unknown): unknown => (isVariable(value) ? `${value.name} (${value.scope})` : value);

export const isEmptyDisplayValue = (value: unknown): boolean => value === undefined || value === '';

export const commonCategories: { key: CommonCategoryKey & keyof ComparerResults; label: string }[] = [
  { key: 'queues', label: 'Queues' },
  { key: 'semaphores', label: 'Semaphores' },
  { key: 'signals', label: 'Signals' },
  { key: 'userEvents', label: 'User events' },
  { key: 'syncEvents', label: 'Sync events' }
];

export const summaryHasChanges = (summary: CategorySummary): boolean =>
  summary.added + summary.removed + summary.modified > 0;

export const getChangedItems = <T extends { status: ComparerStatus }>(items: T[]): T[] =>
  items.filter((item) => item.status !== 'unchanged');

export const getVisibleItems = <T extends { status: ComparerStatus }>(items: T[], showUnchanged: boolean): T[] =>
  showUnchanged ? items : getChangedItems(items);

export const getVisibleParams = (params: ComparerParam[] | undefined, showUnchanged: boolean): ComparerParam[] => {
  if (!params) return [];
  return showUnchanged ? params : params.filter((param) => param.changed);
};

export const statusColors = (status: ComparerStatus) => {
  switch (status) {
    case 'added':
      return 'success';
    case 'removed':
      return 'error';
    case 'modified':
      return 'warning';
    default:
      return 'default';
  }
};

export const sortComparerIds = (array: ComparerNode[]): ComparerNode[] => {
  return array.sort((a, b) => {
    if (a.id.toString()[0] !== '#' || b.id.toString()[0] !== '#') {
      return a.id.toString().localeCompare(b.id.toString());
    }

    const idA = parseInt(a.id.toString().replace('#', ''), 10);
    const idB = parseInt(b.id.toString().replace('#', ''), 10);
    return idA - idB;
  });
};

export const toSummary = (items: ComparerValues[]): CategorySummary => ({
  added: items.filter((item) => item.status === 'added').length,
  removed: items.filter((item) => item.status === 'removed').length,
  modified: items.filter((item) => item.status === 'modified').length
});

export const getNodesSummary = (subroutines: ComparerSubroutine[]): CategorySummary => {
  const nodes: ComparerNode[] = [];
  subroutines.forEach((subroutine) => {
    const nodeChanges = subroutine.changes?.nodeChanges;
    nodeChanges && nodes.push(...nodeChanges);
  });

  return toSummary(nodes);
};

export const getVariablesSummary = (variables: ComparerVariables): CategorySummary => {
  const result: CategorySummary = { added: 0, removed: 0, modified: 0 };
  const scopes = ['config', 'global', 'task', 'channel', 'session'] as const;

  scopes.forEach((scope) => {
    const summary = toSummary(variables[scope]);
    result.added += summary.added;
    result.removed += summary.removed;
    result.modified += summary.modified;
  });

  const localVariables = getLocalVariablesSummary(variables['local']);
  result.added += localVariables.added;
  result.removed += localVariables.removed;
  result.modified += localVariables.modified;

  return result;
};

export const getPromptsSummary = (prompts: ComparerPrompts): CategorySummary => {
  const result: CategorySummary = { added: 0, removed: 0, modified: 0 };

  [prompts.ungrouped, ...prompts.groups.map((group) => group.prompts)].forEach((values) => {
    const summary = toSummary(values);
    result.added += summary.added;
    result.removed += summary.removed;
    result.modified += summary.modified;
  });

  return result;
};

export const getLocalVariablesSummary = (variables: Record<SubroutineId, ComparerScopedVariable[]>) => {
  const result: CategorySummary = { added: 0, removed: 0, modified: 0 };
  Object.values(variables).forEach((values) => {
    const summary = toSummary(values);
    result.added += summary.added;
    result.removed += summary.removed;
    result.modified += summary.modified;
  });

  return result;
};
